import { isExplicitlyClearedCapture } from '@/shared/captureRegion';
import { filterMedia, filterSources } from '@/shared/functions';
import { getDisplayDateAndTime } from '@/shared/clientFunctions';
import { hydrateSourcesContentFromMedia } from '@/shared/timeline-event-payload';
import { resolveHideFromTimeline } from '@/shared/timeline-visibility';
import type { MediaSource, MediaType, Slide } from '@/shared/types';

export type MapTimelineEventRecordOptions = {
  /** Used when the API record has no ``id`` (e.g. optimistic timeline rows). */
  fallbackId?: string;
  /** Preserve fields omitted from a partial PUT response. */
  mergeWith?: Slide;
};

const recordHasKey = (event: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(event, key);

/**
 * A PUT that cleared ``capture`` must stay cleared when the response omits the
 * empty string. An echoed non-empty capture is left alone — that is the server
 * keeping a value, not dropping the clear.
 */
function withExplicitCaptureClears(
  media: MediaSource[],
  requested: MediaSource[] | undefined,
): MediaSource[] {
  if (!requested?.length) return media;
  let changed = false;
  const next = media.map((item, index) => {
    const requestedItem = requested[index];
    if (!isExplicitlyClearedCapture(requestedItem) || isExplicitlyClearedCapture(item)) {
      return item;
    }
    const echoed = item?.mediaContext?.capture;
    if (typeof echoed === 'string' && echoed.trim()) return item;
    changed = true;
    const requestedContext = requestedItem?.mediaContext ?? {};
    return {
      ...item,
      mediaContext: {
        ...(item.mediaContext ?? {}),
        capture: '',
        ...(Object.prototype.hasOwnProperty.call(requestedContext, 'canvas')
          ? { canvas: requestedContext.canvas }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(requestedContext, 'stillFrame')
          ? { stillFrame: requestedContext.stillFrame }
          : {}),
      },
    };
  });
  return changed ? next : media;
}

const buildSlideMediaFromRecord = (
  event: Record<string, unknown>,
  options?: { mergeWith?: Slide },
): MediaSource[] | undefined => {
  if (recordHasKey(event, 'media')) {
    if (Array.isArray(event.media)) {
      return filterMedia(event.media as MediaSource[]);
    }
    return undefined;
  }
  // Partial PUT bodies omit ``media`` but may echo legacy top-level fields that
  // describe only the first clip; do not replace a multi-item mergeWith payload.
  if (options?.mergeWith) {
    return undefined;
  }
  if (event.mediaType && event.mediaUrl) {
    return [
      {
        mediaType: event.mediaType as MediaType,
        mediaSrc: (event.mediaSrc || event.mediaUrl) as string,
        mediaUrl: (event.mediaSrc || event.mediaUrl) as string,
      },
    ];
  }
  return undefined;
};

const buildSlideSourcesFromRecord = (
  event: Record<string, unknown>,
  slideMedia: MediaSource[],
): MediaSource[] | undefined => {
  if (!recordHasKey(event, 'sources')) return undefined;
  if (!Array.isArray(event.sources)) return undefined;
  return hydrateSourcesContentFromMedia(
    filterSources(event.sources as MediaSource[]) as Record<string, unknown>[],
    slideMedia as unknown as Record<string, unknown>[],
  );
};

export const mapTimelineEventRecordToSlide = (
  event: Record<string, unknown>,
  displayOrderFallback = 0,
  options?: MapTimelineEventRecordOptions,
): Slide => {
  const mergeWith = options?.mergeWith;
  const hasEventDateTimeKey = recordHasKey(event, 'eventDateTime');
  const hasEventDateTimeString =
    hasEventDateTimeKey && typeof event.eventDateTime === 'string' && event.eventDateTime.trim() !== '';
  const isExplicitlyUndated = hasEventDateTimeKey && !hasEventDateTimeString;
  const eventDateTime = hasEventDateTimeString ? event.eventDateTime as string : '';
  const resolvedDate = hasEventDateTimeKey
    ? (hasEventDateTimeString ? eventDateTime : undefined)
    : mergeWith?.date;
  const dateParts = hasEventDateTimeString
    ? getDisplayDateAndTime(eventDateTime, null)
    : isExplicitlyUndated
      ? getDisplayDateAndTime('', null)
      : mergeWith
        ? {
            displayDate: mergeWith.displayDate ?? '',
            time: mergeWith.time ?? '',
            localizedDateTime: mergeWith.localizedDateTime ?? '',
          }
        : getDisplayDateAndTime('', null);

  const recordMedia = buildSlideMediaFromRecord(event, { mergeWith });
  const slideMedia = withExplicitCaptureClears(
    recordMedia !== undefined ? recordMedia : (mergeWith?.media ?? []),
    mergeWith?.media,
  );
  const slideSources =
    buildSlideSourcesFromRecord(event, slideMedia)
    ?? mergeWith?.sources
    ?? [];

  const displayOrder =
    typeof event.displayOrder === 'number'
      ? event.displayOrder
      : (mergeWith?.displayOrder ?? displayOrderFallback);

  const show = recordHasKey(event, 'hidden')
    ? !event.hidden
    : (mergeWith?.show ?? true);

  const id = event.id
    ? String(event.id)
    : (mergeWith?.id ?? options?.fallbackId);

  return {
    show,
    order: displayOrder,
    displayOrder,
    approval: recordHasKey(event, 'approved')
      ? (event.approved === true
          ? 'approved'
          : event.approved === false
            ? undefined
            : (event.approved as Slide['approval']))
      : mergeWith?.approval,
    id,
    date: resolvedDate,
    displayDate: hasEventDateTimeKey
      ? dateParts.displayDate
      : (dateParts.displayDate || mergeWith?.displayDate),
    time: hasEventDateTimeKey
      ? dateParts.time
      : (dateParts.time || mergeWith?.time),
    isTimeValid: recordHasKey(event, 'isTimeValid')
      ? event.isTimeValid === true
      : (mergeWith?.isTimeValid ?? false),
    localizedDateTime: hasEventDateTimeKey
      ? dateParts.localizedDateTime
      : (dateParts.localizedDateTime || mergeWith?.localizedDateTime),
    eventLocalTimezone: recordHasKey(event, 'eventLocalTimezone')
      ? (typeof event.eventLocalTimezone === 'string'
          ? event.eventLocalTimezone
          : undefined)
      : mergeWith?.eventLocalTimezone,
    eventType: recordHasKey(event, 'eventType')
      ? (typeof event.eventType === 'string' ? event.eventType : undefined)
      : mergeWith?.eventType,
    slideTitle: recordHasKey(event, 'slideTitle')
      ? (event.slideTitle as string | undefined)
      : mergeWith?.slideTitle,
    slideSubTitle: recordHasKey(event, 'slideSubTitle')
      ? (event.slideSubTitle as string | undefined)
      : mergeWith?.slideSubTitle,
    slideDescription: recordHasKey(event, 'slideDescription')
      ? (event.slideDescription as string | undefined)
      : mergeWith?.slideDescription,
    courtroomTitle: recordHasKey(event, 'courtroomTitle')
      ? (event.courtroomTitle as string | undefined)
      : mergeWith?.courtroomTitle,
    courtroomDescription: recordHasKey(event, 'courtroomDescription')
      ? (event.courtroomDescription as string | undefined)
      : mergeWith?.courtroomDescription,
    media: slideMedia,
    mediaSrc: slideMedia[0]
      ? slideMedia[0].mediaSrc || slideMedia[0].mediaUrl
      : mergeWith?.mediaSrc,
    mediaType: slideMedia[0]
      ? slideMedia[0].mediaType
      : mergeWith?.mediaType,
    location: recordHasKey(event, 'location')
      ? (event.location as Slide['location'])
      : mergeWith?.location,
    sources: slideSources,
    hideFromTimeline: resolveHideFromTimeline({
      serverValue: event.hiddenFromTimeline,
      fallback: mergeWith?.hideFromTimeline,
    }),
    createdAt: recordHasKey(event, 'createdAt')
      ? (event.createdAt as string | undefined)
      : mergeWith?.createdAt,
    updatedAt: recordHasKey(event, 'updatedAt')
      ? (event.updatedAt as string | undefined)
      : mergeWith?.updatedAt,
  };
};
